import type { JSX } from 'react';
import { DateTime } from '@ksyos/date-time';
import { Button, ButtonRow, SectionHeader } from '@ksyos/design-system';
import { useSearchParams } from '@ksyos/router';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Scroll from 'react-scroll';
import type { OutpatientClinicTaskWithAuthorizationAndNotes } from '@common/contexts/support/domain/types/customOutpatientClinicTaskTypes';
import type { EpisodeCategory } from '@common/enums';
import type { IEpisodeCategoryBasic } from '@common/interfaces';
import type { BrightPlanOperationType } from '@common/interfaces/api/brightPlanApi';
import type { IPatient } from '@common/interfaces/patient';
import type { IPatientContact } from '@frontend/components/interfaces';
import type { IReduxState } from '@frontend/configureStore';
import { outpatientClinicTaskMapper } from '@common/contexts/support/infrastructure/mappers/outpatientClinicTaskMapper';
import { PatientTaskStatus } from '@common/enums';
import { hasBrightPlanAccess } from '@frontend/services/authorizationService';
import { getOperationTypes, scrollContainerId, scrollToWithinContainer } from '@frontend/utils';
import { handleBrightPlanGetSsoUrl } from '@frontend/utils/handleBrightPlanGetSsoUrl';
import { useFeatureFlag } from '@frontend/utils/hooks/useFeatureFlag';
import { useFrontendSettings } from '@frontend/utils/hooks/useFrontendSettings';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { PatientContactItem } from '../patientContacts/PatientContactItem';
import { PatientContactItemAddEdit } from '../patientContacts/PatientContactItemAddEdit';
import { PatientTaskItemAddEdit } from '../patientTasks/PatientTaskItemAddEdit';
import { BrightPlanPlanActionModal } from './BrightPlanPlanActionModal';
import { EmptyContactsAndTasks } from './EmptyContactsAndTasks';
import { MissingPatientDetailsModal } from './MissingPatientDetailsModal';
import { PatientTask } from './PatientTask';

type ContactsAndTasksProps = Readonly<{
    episodeCategoryName: EpisodeCategory;
    isFlexibleCarePath: boolean;
    patient: IPatient;
}>;

export function ContactsAndTasks({
    episodeCategoryName,
    isFlexibleCarePath,
    patient,
}: ContactsAndTasksProps): JSX.Element | null {
    const [searchParams] = useSearchParams();
    const { frontendSettings } = useFrontendSettings();

    const [canAddContact, setCanAddContact] = useState(false);
    const [canAddTask, setCanAddTask] = useState(false);
    const [canAddTaskNote, setCanAddTaskNote] = useState(false);
    const [canReadContact, setCanReadContact] = useState(false);
    const [canReadTask, setCanReadTask] = useState(false);
    const [canReadTaskNote, setCanReadTaskNote] = useState(false);
    const [closedTasks, setClosedTasks] = useState<OutpatientClinicTaskWithAuthorizationAndNotes[]>([]);
    const [createTask, setCreateTask] = useState(false);
    const [episodeCategory, setEpisodeCategory] = useState<IEpisodeCategoryBasic>();
    const [hasScrolled, setHasScrolled] = useState(false);
    const [isCreateTaskButtonDisabled, setIsCreateTaskButtonDisabled] = useState(false);
    const [tasks, setTasks] = useState<OutpatientClinicTaskWithAuthorizationAndNotes[]>([]);
    const [contacts, setContacts] = useState<IPatientContact[]>([]);
    const [openTasks, setOpenTasks] = useState<OutpatientClinicTaskWithAuthorizationAndNotes[]>([]);
    const [registerContact, setRegisterContact] = useState(false);
    const [showScheduleBrightPlanActionModal, setShowScheduleBrightPlanActionModal] = useState(false);
    const [showMissingPatientData, setShowMissingPatientData] = useState(false);
    const [brightPlanOperationTypes, setBrightPlanOperationTypes] = useState<BrightPlanOperationType[]>([]);

    const organizationId = useSelector((state: IReduxState) => state.authentication.organizationId);
    const profession = useSelector((state: IReduxState) => state.authentication.profession);
    const scrollRef = useRef(null);

    const scrollItemId = searchParams.get('itemId');
    const enableBrightPlanPatientScheduleAction = useFeatureFlag('ENABLE_BRIGHTPLAN_PATIENT_SCHEDULE_ACTION', false);
    const showBrightPlanPatientButton = enableBrightPlanPatientScheduleAction && brightPlanOperationTypes.length > 0;

    useEffect(() => {
        loadEpisodeCategory();

        if (enableBrightPlanPatientScheduleAction) {
            loadBrightPlanOperationTypes(episodeCategoryName);
        }
    }, [episodeCategoryName, organizationId]);

    useEffect(() => {
        loadContactsAndTasks();
    }, [episodeCategory]);

    useEffect(() => {
        if (scrollRef.current && scrollItemId && !hasScrolled) {
            scrollToWithinContainer(scrollContainerId, scrollItemId);
            setHasScrolled(true);
        }
    });

    useEffect(() => {
        setOpenTasks(getOpenTasks());
        setClosedTasks(getClosedTasks());
    }, [tasks, contacts]);

    if (!episodeCategory) {
        return null;
    }

    function renderTask(item: OutpatientClinicTaskWithAuthorizationAndNotes): JSX.Element | null {
        if (!episodeCategory || !patient || !organizationId) {
            return null;
        }

        return (
            <Scroll.Element
                key={item.id}
                name={item.id}
                ref={scrollItemId && scrollItemId === item.id ? scrollRef : undefined}
            >
                <PatientTask
                    canAddTaskNote={canAddTaskNote}
                    canReadTaskNote={canReadTaskNote}
                    episodeCategory={episodeCategory}
                    onEditStarted={(value) => setIsCreateTaskButtonDisabled(value)}
                    onSave={async () => {
                        await loadContactsAndTasks();
                        setIsCreateTaskButtonDisabled(false);
                    }}
                    organizationId={organizationId}
                    patientId={patient.id}
                    task={item}
                />
            </Scroll.Element>
        );
    }

    function renderContact(item: IPatientContact): JSX.Element | null {
        if (!episodeCategory || !patient || !organizationId) {
            return null;
        }

        return (
            <Scroll.Element
                key={item.id}
                name={item.id}
                ref={scrollItemId && scrollItemId === item.id ? scrollRef : undefined}
            >
                <PatientContactItem
                    canUpdate={item.authorization?.canUpdate}
                    contactItem={item}
                    episodeCategoryId={episodeCategory.id}
                    onSave={async () => {
                        setRegisterContact(false);
                        await loadContactsAndTasks();
                    }}
                    onUpdate={async () => {
                        await loadContactsAndTasks();
                    }}
                    patientId={patient.id}
                />
            </Scroll.Element>
        );
    }

    return (
        <>
            {canReadTask && (
                <>
                    <ButtonRow
                        justify="right"
                        sx={{ mb: 4 }}
                    >
                        {profession &&
                            hasBrightPlanAccess(profession, frontendSettings.brightPlanIntegrationEnabled) && (
                                <>
                                    {showBrightPlanPatientButton && (
                                        <Button
                                            iconLeft="invitation"
                                            onClick={() =>
                                                (!patient?.homePhoneNumber && !patient?.mobileNumber) ||
                                                !patient?.emailAddress
                                                    ? setShowMissingPatientData(true)
                                                    : setShowScheduleBrightPlanActionModal(true)
                                            }
                                            style={{ float: 'right' }}
                                            variant="outline"
                                        >
                                            Patiënt plant afspraak
                                        </Button>
                                    )}
                                    <Button
                                        iconLeft="external"
                                        onClick={async () =>
                                            await handleBrightPlanGetSsoUrl(
                                                patient.id,
                                                episodeCategory.name,
                                                isFlexibleCarePath
                                            )
                                        }
                                        style={{ float: 'right' }}
                                        variant="outline"
                                    >
                                        Plan afspraak
                                    </Button>
                                </>
                            )}
                    </ButtonRow>
                    <SectionHeader
                        action={
                            canAddTask &&
                            !createTask &&
                            !isCreateTaskButtonDisabled && (
                                <Button
                                    color="secondary"
                                    iconLeft="add"
                                    isDisabled={isCreateTaskButtonDisabled || createTask}
                                    onClick={() => setCreateTask(true)}
                                    variant="link"
                                >
                                    {translateKey('contactsAndTasks.newTask')}
                                </Button>
                            )
                        }
                        noGutterOnMobile={true}
                        title={translateKey('contactsAndTasks.openTasks')}
                    />

                    {createTask && organizationId && (
                        <PatientTaskItemAddEdit
                            episodeCategory={episodeCategory}
                            onCancel={() => setCreateTask(false)}
                            onSave={async () => {
                                setCreateTask(false);
                                await loadContactsAndTasks();
                            }}
                            organizationId={organizationId}
                            patientId={patient.id}
                        />
                    )}

                    {openTasks.length === 0 && !createTask && <EmptyContactsAndTasks message={'openTasks'} />}

                    {openTasks.map((item) => renderTask(item))}
                </>
            )}

            {canReadContact && (
                <>
                    <SectionHeader
                        action={
                            canAddContact &&
                            !registerContact && (
                                <Button
                                    color="secondary"
                                    iconLeft="add"
                                    isDisabled={registerContact}
                                    onClick={() => setRegisterContact(true)}
                                    variant="link"
                                >
                                    {translateKey('contactsAndTasks.registerContact')}
                                </Button>
                            )
                        }
                        noGutterOnMobile={true}
                        title={translateKey('contactsAndTasks.contacts')}
                    />

                    {registerContact && (
                        <PatientContactItemAddEdit
                            episodeCategoryId={episodeCategory.id}
                            formId="newPatientContact"
                            isEditing={false}
                            onCancel={() => setRegisterContact(false)}
                            onSave={async () => {
                                await setRegisterContact(false);
                                await loadContactsAndTasks();
                            }}
                            onUpdate={async () => {
                                await loadContactsAndTasks();
                            }}
                            patientId={patient.id}
                        />
                    )}

                    {contacts.map((item) => renderContact(item))}

                    {contacts.length === 0 && !registerContact && <EmptyContactsAndTasks message={'contacts'} />}
                </>
            )}

            <SectionHeader title={translateKey('contactsAndTasks.previouslyCompleted')} />

            {closedTasks.map((item) => renderTask(item))}

            {closedTasks.length === 0 && !createTask && <EmptyContactsAndTasks message={'closedTasks'} />}

            <MissingPatientDetailsModal
                isOpen={showMissingPatientData}
                onClose={setShowMissingPatientData}
                patient={patient}
            />

            <BrightPlanPlanActionModal
                episodeCategoryId={episodeCategory.id}
                isFlexibleCarePath={isFlexibleCarePath}
                isOpen={showScheduleBrightPlanActionModal}
                onClose={() => setShowScheduleBrightPlanActionModal(false)}
                onSave={async () => {
                    await loadContactsAndTasks();
                }}
                operationTypes={brightPlanOperationTypes}
                patientId={patient.id}
            />
        </>
    );

    async function loadEpisodeCategory(): Promise<void> {
        const result = await fetchJson(`/api/episodeCategories?name=${episodeCategoryName}`, 'GET');

        if (result.json.items.length === 0) {
            throw new Error(`Could not find episode category with name: ${episodeCategoryName}`);
        }

        setEpisodeCategory(result.json.items[0]);
    }

    async function loadContactsAndTasks(): Promise<void> {
        if (!episodeCategory) {
            return;
        }

        const url = `/api/patients/${patient.id}/contactsAndTasks?episodeCategoryId=${episodeCategory.id}`;
        const result = await fetchJson(url, 'GET');

        if (!(result.json?.tasks && result.json?.contacts)) {
            return;
        }

        setCanAddContact(result.json.authorizations.contactAuthorization.canAdd);
        setCanAddTask(result.json.authorizations.taskAuthorization.canAdd);
        setCanAddTaskNote(result.json.authorizations.taskNoteAuthorization.canAdd);
        setCanReadContact(result.json.authorizations.contactAuthorization.canRead);
        setCanReadTask(result.json.authorizations.taskAuthorization.canRead);
        setCanReadTaskNote(result.json.authorizations.taskNoteAuthorization.canRead);
        setTasks(
            result.json.tasks.map((entry: OutpatientClinicTaskWithAuthorizationAndNotes) => ({
                ...outpatientClinicTaskMapper(entry),
                authorization: entry.authorization,
                notes: entry.notes.map((note) => ({
                    ...note,
                    created_at: note.created_at ? DateTime.from(note.created_at) : null,
                    updated_at: note.updated_at ? DateTime.from(note.updated_at) : null,
                })),
            }))
        );
        setContacts(result.json.contacts);
    }

    function getOpenTasks() {
        return tasks
            .filter((task) => task.status === PatientTaskStatus.open)
            .sort((a, b) => {
                if (a.expectedDateCompleted > b.expectedDateCompleted) {
                    return 1;
                } else if (a.expectedDateCompleted < b.expectedDateCompleted) {
                    return -1;
                } else {
                    return 0;
                }
            });
    }

    function getClosedTasks() {
        return tasks.filter((task) => task.status === PatientTaskStatus.completed);
    }

    async function loadBrightPlanOperationTypes(category: string): Promise<void> {
        const operationTypes = await getOperationTypes({ category });
        setBrightPlanOperationTypes(operationTypes);
    }
}
